import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/modules/shared/components/ui/button";
import { X } from "lucide-react";

/**
 * AppModal - Global Standardized Modal Component (MANDATORY)
 * 
 * Per Global UI Standardization Enforcement - Modal Standard Rules:
 * - Max width: 600px (default)
 * - Large: 900px
 * - Padding: 16px
 * - Title: 14px
 * - Body text: 12px
 * - Footer actions right aligned
 * - Button gap: 8px
 * 
 * All popups must use AppModal.
 * No direct usage of raw modal component allowed.
 */

type ModalSize = "sm" | "default" | "lg" | "xl";

interface AppModalProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Modal size: sm (400px), default (600px), lg (900px), xl (1100px) */
  size?: ModalSize;
  /** Allow closing on backdrop/outside click. Defaults to false for data safety. */
  closeOnOutsideClick?: boolean;
  /** Allow closing with Escape key. Defaults to false for data safety. */
  closeOnEscape?: boolean;
  /** Show close button in modal headers by default. */
  showCloseButton?: boolean;
  /** @deprecated Use closeOnOutsideClick instead. */
  preventBackdropClose?: boolean;
  /** Backdrop appearance. Use transparent when nesting over a sheet or drawer. */
  backdropVariant?: "dimmed" | "transparent";
  /**
   * Called before a backdrop-click or Escape close is applied (not before a programmatic
   * onClose call, e.g. a Cancel button). Return false to block the close — use this to warn
   * about unsaved changes instead of letting the dialog dismiss silently.
   */
  onBeforeClose?: () => boolean;
  className?: string;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: "max-w-[400px]",
  default: "max-w-[600px]",
  lg: "max-w-[900px]",
  xl: "max-w-[1100px]",
};

const AppModalContext = React.createContext<{ onClose: () => void; showCloseButton: boolean } | null>(null);

export function AppModal({
  open,
  onClose,
  children,
  size = "default",
  closeOnOutsideClick = false,
  closeOnEscape = false,
  showCloseButton = true,
  preventBackdropClose,
  backdropVariant = "dimmed",
  onBeforeClose,
  className,
}: AppModalProps) {
  const allowOutsideClose = closeOnOutsideClick && !preventBackdropClose;

  const requestClose = React.useCallback(() => {
    if (onBeforeClose && onBeforeClose() === false) return;
    onClose();
  }, [onBeforeClose, onClose]);

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open && closeOnEscape) {
        requestClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [closeOnEscape, open, requestClose]);

  // Prevent body scroll when modal is open (with scrollbar compensation)
  React.useEffect(() => {
    if (open) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <AppModalContext.Provider value={{ onClose, showCloseButton }}>
      <div className="fixed inset-0 z-[1400]" role="dialog" aria-modal="true">
        {/* Backdrop – no backdrop-filter to avoid GPU flicker */}
        <div
          className={cn(
            "fixed inset-0 animate-in fade-in-0 duration-150",
            backdropVariant === "dimmed" ? "bg-black/50" : "bg-transparent"
          )}
          onClick={() => allowOutsideClose && requestClose()}
          aria-hidden="true"
        />
        
        {/* Modal container */}
        <div className="fixed inset-0 z-[1410] flex items-center justify-center p-4 overflow-y-auto pointer-events-none">
          <div
            className={cn(
              "relative w-full bg-surface-premium text-card-foreground rounded-xl shadow-xl border border-border-hairline pointer-events-auto",
              "animate-in zoom-in-95 fade-in-0 duration-150",
              sizeClasses[size],
              className
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </div>
        </div>
      </div>
    </AppModalContext.Provider>,
    document.body,
  );
}

/**
 * AppModalHeader - Modal header with title and close button
 * 
 * Per Global UI: Title = 14px, 600 weight
 */
interface AppModalHeaderProps {
  children?: React.ReactNode;
  className?: string;
  /** Hide close button */
  hideCloseButton?: boolean;
}

export function AppModalHeader({
  children,
  className,
  hideCloseButton = false,
}: AppModalHeaderProps) {
  const context = React.useContext(AppModalContext);

  return (
    <div className={cn("flex items-start justify-between gap-3 p-4 border-b border-border-hairline", className)}>
      <div className="min-w-0 flex-1">
        {children}
      </div>
      {!hideCloseButton && context?.showCloseButton && (
        <button
          type="button"
          onClick={context?.onClose}
          aria-label="Close modal"
          className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0 p-1 rounded-lg hover:bg-surface-hover flex items-center justify-center"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

/**
 * AppModalTitle - Modal title
 * 
 * Per Global UI: 14px, 600 weight
 */
interface AppModalTitleProps {
  children: React.ReactNode;
  className?: string;
}

export function AppModalTitle({ children, className }: AppModalTitleProps) {
  return (
    <h2 className={cn("app-section-title", className)}>
      {children}
    </h2>
  );
}

/**
 * AppModalDescription - Modal description
 * 
 * Per Global UI: 12px, muted
 */
interface AppModalDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

export function AppModalDescription({ children, className }: AppModalDescriptionProps) {
  return (
    <p className={cn("app-body-text text-muted-foreground mt-0.5", className)}>
      {children}
    </p>
  );
}

/**
 * AppModalBody - Modal body content
 * 
 * Per Global UI: Padding = 16px, Body text = 12px
 */
interface AppModalBodyProps {
  children: React.ReactNode;
  className?: string;
  /** Remove padding for tables/lists */
  noPadding?: boolean;
}

export function AppModalBody({ children, className, noPadding = false }: AppModalBodyProps) {
  return (
    <div className={cn(
      "app-body-text",
      !noPadding && "p-4",
      "max-h-[calc(100vh-200px)] overflow-y-auto",
      className
    )}>
      {children}
    </div>
  );
}

/**
 * AppModalFooter - Modal footer with actions
 * 
 * Per Global UI: 
 * - Footer actions right aligned
 * - Button gap: 8px
 */
interface AppModalFooterProps {
  children: React.ReactNode;
  className?: string;
  /** Align buttons left instead of right */
  alignLeft?: boolean;
}

export function AppModalFooter({ children, className, alignLeft = false }: AppModalFooterProps) {
  return (
    <div className={cn(
      "flex items-center gap-2 p-4 border-t border-border-hairline",
      alignLeft ? "justify-start" : "justify-end",
      className
    )}>
      {children}
    </div>
  );
}

/**
 * AppConfirmModal - Pre-built confirmation modal
 */
interface AppConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  showCancelButton?: boolean;
  variant?: "default" | "danger";
  loading?: boolean;
}

export function AppConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  showCancelButton = true,
  variant = "default",
  loading = false,
}: AppConfirmModalProps) {
  return (
    <AppModal open={open} onClose={onClose} size="sm">
      <AppModalHeader>
        <AppModalTitle>{title}</AppModalTitle>
      </AppModalHeader>
      <AppModalBody>
        <p>{message}</p>
      </AppModalBody>
      <AppModalFooter>
        {showCancelButton ? (
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            {cancelText}
          </Button>
        ) : null}
        <Button
          variant={variant === "danger" ? "danger" : "default"}
          onClick={onConfirm}
          loading={loading}
        >
          {confirmText}
        </Button>
      </AppModalFooter>
    </AppModal>
  );
}

export default AppModal;
